<template>
  <div v-if="np.id" class="gmp">
    <div v-if="showComments" class="gmp-comments"><TrackComments :key="np.id" :trackId="np.id" :showHeader="true" :startOpen="true" /></div>
    <div class="gmp-progress" @click.stop="onSeek"><div class="gmp-bar" :style="{ width: pct + '%' }"></div></div>
    <div class="gmp-row" @click="expanded = true">
      <div class="gmp-thumb" :style="miniBg(np)">{{ np.cover ? "" : "♪" }}</div>
      <div class="gmp-info" role="button" tabindex="0" aria-label="Open full screen player" @keyup.enter="expanded = true">
        <div class="gmp-title">{{ np.title }}</div>
        <div class="gmp-artist">{{ np.artist }}</div>
      </div>
      <div class="gmp-time">{{ fmt(state.currentTime) }} / {{ fmt(state.duration) }}</div>
      <button class="gmp-btn" @click.stop="prev" aria-label="Previous">⏮</button>
      <button class="gmp-btn gmp-main" @click.stop="toggle" :aria-label="np.paused ? 'Play' : 'Pause'">{{ np.paused ? "▶" : "❚❚" }}</button>
      <button class="gmp-btn" @click.stop="next" aria-label="Next">⏭</button>
      <button class="gmp-btn" :class="{ 'gmp-on': showComments }" @click.stop="showComments = !showComments" aria-label="Comments">💬</button>
      <button class="gmp-btn" @click.stop="expanded = true" aria-label="Open full screen player" title="Full screen &amp; lyrics">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 15l7-7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <button class="gmp-btn gmp-close" @click.stop="stop" aria-label="Close">✕</button>
    </div>
    <FullPlayer :open="expanded" @close="expanded = false" @toggle-comments="expanded = false; showComments = true" />
  </div>
</template>

<script>
import playerStore from "@/services/playerStore";
import { coverBg } from "@/services/cover";
import { auth, db } from "@/firebase";
import { doc, updateDoc, increment, addDoc, collection, serverTimestamp, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { reportListening } from "@/services/poolService";
import TrackComments from "@/components/TrackComments.vue";
import FullPlayer from "@/components/FullPlayer.vue";
import { syncPushToken } from "@/services/pushService";

export default {
  name: "GlobalMiniPlayer",
  components: { TrackComments, FullPlayer },
  computed: {
    state() { return playerStore.state; },
    np() { return playerStore.state.nowPlaying; },
    pct() { return playerStore.state.duration ? (playerStore.state.currentTime / playerStore.state.duration) * 100 : 0; },
  },
  data() { return { isMember: false, showComments: false, expanded: false }; },
  mounted() {
    // Only paying members' listening is counted (the server checks again).
    this._authOff = onAuthStateChanged(auth, (u) => {
      if (this._userOff) { this._userOff(); this._userOff = null; }
      this.isMember = false;
      this.setMyPhoto("");
      if (!u) return;
      try { if (localStorage.getItem("mzPushOff") !== "1") syncPushToken(u.uid); } catch (e) { /* storage blocked */ }
      this._userOff = onSnapshot(doc(db, "user", u.uid), (s) => {
        const d = s.data() || {};
        this.isMember = !!d.isMember;
        this.setMyPhoto(d.image || d.photoURL || d.photoUrl || "");
      }, () => {});
    });
    playerStore.onListen(({ trackId, seconds }) => {
      if (!this.isMember) return;
      reportListening(trackId, seconds).catch(() => { /* best-effort */ });
    });
  },
  beforeDestroy() { if (this._authOff) this._authOff(); if (this._userOff) this._userOff(); },
  created() {
    playerStore.onPlay(async (entry) => {
      try {
        await updateDoc(doc(db, "tracks", entry.id), { plays: increment(1) });
      } catch (e) { /* listener may not own track; rules decide */ }
      const uid = auth.currentUser && auth.currentUser.uid;
      if (!uid) return;
      try {
        await addDoc(collection(db, "playEvents"), {
          trackId: entry.id, artistId: entry.artistId || "", listenerId: uid, createdAt: serverTimestamp(),
        });
      } catch (e) { /* analytics is best-effort */ }
      try {
        await addDoc(collection(db, "user", uid, "history"), {
          trackId: entry.id, title: entry.title, artist: entry.artist, playedAt: serverTimestamp(),
        });
      } catch (e) { /* ignore */ }
    });
  },
  methods: {
    // Puts the signed-in user's photo on every sidebar avatar (.su-av) without each page having to load it.
    setMyPhoto(url) {
      try {
        const root = document.documentElement;
        if (url) { root.style.setProperty("--my-photo", `url("${String(url).replace(/"/g, "%22")}")`); root.classList.add("has-my-photo"); }
        else { root.style.removeProperty("--my-photo"); root.classList.remove("has-my-photo"); }
      } catch (e) { /* ignore */ }
    },
    miniBg(np) { return coverBg(np.cover, np.color); },
    toggle() { playerStore.toggle(); },
    next() { playerStore.playNext(); },
    prev() { playerStore.playPrevious(); },
    stop() { this.expanded = false; playerStore.stop(); },
    onSeek(e) {
      const r = e.currentTarget.getBoundingClientRect();
      playerStore.seekToPct((e.clientX - r.left) / r.width);
    },
    fmt(s) {
      s = Math.floor(s || 0);
      return Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0");
    },
  },
};
</script>

<style>
.has-my-photo .su-av{background-image:var(--my-photo) !important;background-size:cover !important;background-position:center !important;color:transparent !important;font-size:0 !important}
</style>

<style scoped>
.gmp { position: fixed; left: 0; right: 0; bottom: 0; z-index: 9999; background: #14141c; border-top: 1px solid #2a2a38; color: #fff; }
.gmp-progress { height: 4px; background: #2a2a38; cursor: pointer; }
.gmp-bar { height: 100%; background: #7c5cff; }
.gmp-row { display: flex; align-items: center; gap: 12px; padding: 10px 16px; cursor: pointer; transition: background .15s; }
.gmp-row:hover { background: rgba(255,255,255,0.03); }
.gmp-info:focus-visible { outline: 2px solid #7c5cff; outline-offset: 2px; border-radius: 6px; }
.gmp-thumb { width: 38px; height: 38px; border-radius: 6px; display: flex; align-items: center; justify-content: center; }
.gmp-info { flex: 1; min-width: 0; }
.gmp-title { font-weight: 600; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gmp-artist { font-size: 12px; color: #9a9ab0; }
.gmp-time { font-size: 12px; color: #9a9ab0; font-variant-numeric: tabular-nums; }
.gmp-btn { background: none; border: none; color: #fff; font-size: 16px; cursor: pointer; padding: 6px; }
.gmp-main { background: #7c5cff; border-radius: 50%; width: 36px; height: 36px; }
.gmp-close { color: #9a9ab0; }
.gmp-on { background: rgba(212,140,28,0.2); border-radius: 8px; }
.gmp-comments { max-height: 55vh; overflow-y: auto; background: #14141c; border-bottom: 1px solid #2a2a38; padding-top: 10px; }
@media (max-width: 600px) { .gmp-time { display: none; } }
</style>